import React, { useMemo } from 'react'
import { useQuery } from '@apollo/client'
import { slugs } from '@/constants/slugs'
import { ServerExperiments } from '@/experimentation'
import { useGuildMember } from '@/services/GuildUserService'
import { GET_THEATRICAL_DISCOUNT_INFO } from '@/services/Theatrical/queries'
import { GetTheatricalDiscountInfoQuery, GetTheatricalDiscountInfoQueryVariables } from '@/types/codegen-federation'
import { ReactFCC } from '@/types/react'
import { getItemOrFirstEntry } from '@/utils/array-utils'
import { useSafeRouter } from '@/utils/nextUtils/safe-router'

const QUERY_PROMO_OVERRIDE: Record<string, Record<string, TheatricalPromotion>> = {
  [slugs.kingOfKings]: {
    buyearlysavebig: {
      promoType: 'EARLY_BIRD',
      code: 'KING',
      hasModal: true,
    },
    giveaway: {
      promoType: 'GIVEAWAY',
      hasModal: true,
    },
  },
}

export type TheatricalPromotionType =
  | 'GIVEAWAY'
  | 'EARLY_BIRD'
  | 'PREMIUM_GUILD'
  | 'FREE_GUILD'
  | 'NO_FEES'
  | 'FLASH_SALE'

export interface TheatricalPromotion {
  promoType: TheatricalPromotionType
  discount?: number
  code?: string
  flag?: string
  hasModal: boolean
}

const TheatricalPromos: Record<string, TheatricalPromotion> = {
  [slugs.ruleBreakers]: {
    promoType: 'GIVEAWAY',
    hasModal: false,
  },
  [slugs.kingOfKings]: {
    promoType: 'GIVEAWAY',
    hasModal: true,
  },
  [slugs.theLastRodeo]: {
    promoType: 'GIVEAWAY',
    hasModal: true,
  },
}

export interface TheatricalPromotionContextValues {
  promo: TheatricalPromotion | undefined
  isReady: boolean
  refetch: () => void
  experiments?: ServerExperiments
}

const TheatricalPromotionContext = React.createContext<TheatricalPromotionContextValues>({
  promo: undefined,
  isReady: false,
  refetch: () => {},
  experiments: undefined,
})

export const useTheatricalPromotionContext = (): TheatricalPromotionContextValues => {
  return React.useContext(TheatricalPromotionContext)
}

interface TheatricalPromotionContextProviderProps {
  theatricalSlug: string
  experiments?: ServerExperiments
}

export const TheatricalPromotionContextProvider: ReactFCC<TheatricalPromotionContextProviderProps> = ({
  children,
  theatricalSlug,
  experiments,
}) => {
  const { query } = useSafeRouter()
  const queryPromo = getItemOrFirstEntry(query?.promo)
  const { guildPermissions, loading: guildLoading } = useGuildMember()

  const {
    data,
    refetch,
    loading: queryLoading,
  } = useQuery<GetTheatricalDiscountInfoQuery, GetTheatricalDiscountInfoQueryVariables>(GET_THEATRICAL_DISCOUNT_INFO, {
    variables: {
      input: { code: 'EARLYBIRD' },
    },
    skip: theatricalSlug !== slugs.theLastRodeo,
  })

  const discountValue = data?.discountByCode?.discount?.value
  const discountType = data?.discountByCode?.discount?.type

  const values = useMemo(() => {
    const handleRefetch = async () => {
      if (refetch) {
        return refetch()
      }
      return undefined
    }

    const isDataLoading = guildLoading || queryLoading

    if (isDataLoading) {
      return {
        promo: undefined,
        isReady: false,
        refetch: handleRefetch,
      }
    }

    const promo = !guildPermissions?.hasFreeTickets
      ? !!discountValue && discountType === 'PERCENTAGE'
        ? ({
            promoType: 'EARLY_BIRD',
            code: 'EARLYBIRD',
            hasModal: true,
            discount: Math.round(discountValue * 100),
          } as TheatricalPromotion)
        : undefined
      : QUERY_PROMO_OVERRIDE?.[theatricalSlug]?.[queryPromo ?? ''] ?? TheatricalPromos?.[theatricalSlug]

    if (theatricalSlug === slugs.kingOfKings && !guildPermissions?.hasFreeTickets) {
      return {
        promo: {
          promoType: 'GIVEAWAY',
          hasModal: true,
        } as TheatricalPromotion,
        isReady: true,
        refetch: handleRefetch,
        experiments,
      }
    }

    return {
      promo,
      isReady: true,
      refetch: handleRefetch,
      experiments,
    }
  }, [
    discountType,
    discountValue,
    guildPermissions?.hasFreeTickets,
    guildLoading,
    queryLoading,
    queryPromo,
    refetch,
    theatricalSlug,
    experiments,
  ])

  return <TheatricalPromotionContext.Provider value={values}>{children}</TheatricalPromotionContext.Provider>
}
